import React from 'react';
import {
  ErrorBoundary as ReactErrorBoundary,
  type FallbackProps,
} from 'react-error-boundary';
import { AlertTriangle, RefreshCcw } from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Button,
} from '@xanectech/ui/components';
import { createLogger } from '@xanectech/lib';

const logger = createLogger('Components - ErrorBoundry');

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallbackComponent?: React.ComponentType<FallbackProps>;
}

const DefaultErrorFallback = ({ error, resetErrorBoundary }: FallbackProps) => {
  return (
    <div className='flex min-h-[400px] w-full items-center justify-center p-4'>
      <Card className='w-full max-w-md border-destructive/50 shadow-lg'>
        <CardHeader className='text-center'>
          <div className='flex justify-center mb-4'>
            <div className='rounded-full bg-destructive/10 p-3'>
              <AlertTriangle className='h-10 w-10 text-destructive' />
            </div>
          </div>
          <CardTitle className='text-2xl font-bold text-foreground'>
            Something went wrong
          </CardTitle>
          <CardDescription className='text-base text-muted-foreground mt-2'>
            An unexpected error has occurred. We've been notified and are
            working to fix it.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className='rounded-md bg-muted p-4 font-mono text-sm text-muted-foreground overflow-auto max-h-40'>
            {(error instanceof Error ? error.message : String(error)) ||
              'Unknown error occurred'}
          </div>
        </CardContent>
        <CardFooter className='flex justify-center'>
          <Button
            onClick={resetErrorBoundary}
            variant='default'
            size='lg'
            className='w-full sm:w-auto gap-2'
          >
            <RefreshCcw className='h-4 w-4' />
            Try Again
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

const ErrorBoundary = ({ children, fallbackComponent }: ErrorBoundaryProps) => {
  const logError = (
    error: unknown,
    info: { componentStack?: string | null },
  ) => {
    const errorMessage = error instanceof Error ? error.message : String(error);
    const errorStack = error instanceof Error ? error.stack : undefined;
    logger.error('Uncaught error in application', {
      message: errorMessage,
      stack: errorStack,
      componentStack: info.componentStack,
    });
  };

  return (
    <ReactErrorBoundary
      FallbackComponent={fallbackComponent || DefaultErrorFallback}
      onError={logError}
    >
      {children}
    </ReactErrorBoundary>
  );
};

export { ErrorBoundary, type ErrorBoundaryProps, DefaultErrorFallback };
